/**
 * TEAM STORE
 *
 * Zustand store for managing workspace team members.
 * Calls backend API (/api/workspaces/:id/members) for CRUD.
 */

import { create } from "zustand";
import { api } from "../lib/api";

export type MemberRole = "owner" | "admin" | "member" | "viewer";
export type InvitationStatus = "accepted" | "pending";

export interface TeamMember {
  id: number;
  workspace_id: number;
  user_id: string | null;
  email: string | null;
  name: string | null;
  role: MemberRole;
  invitation_status: InvitationStatus;
  invited_at: string | null;
  accepted_at: string | null;
  invited_by_name: string | null;
  is_self: boolean;
}

interface FetchMembersResponse {
  members: TeamMember[];
  current_user_role: MemberRole;
}

interface TeamStore {
  members: TeamMember[];
  currentUserRole: MemberRole | null;
  isLoading: boolean;
  error: string | null;
  fetchMembers: (workspaceId: number) => Promise<void>;
  inviteMember: (workspaceId: number, email: string, role: MemberRole) => Promise<void>;
  updateMemberRole: (workspaceId: number, memberId: number, role: MemberRole) => Promise<void>;
  removeMember: (workspaceId: number, memberId: number) => Promise<void>;
}

export const useTeamStore = create<TeamStore>((set) => ({
  members: [],
  currentUserRole: null,
  isLoading: false,
  error: null,

  fetchMembers: async (workspaceId) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<FetchMembersResponse>(`/api/workspaces/${workspaceId}/members`);
      set({ members: data.members, currentUserRole: data.current_user_role, isLoading: false });
    } catch (e: any) {
      set({ error: e.message || "Failed to load members", isLoading: false });
    }
  },

  inviteMember: async (workspaceId, email, role) => {
    const data = await api.post<TeamMember>(`/api/workspaces/${workspaceId}/members`, { email, role });
    set((s) => ({ members: [...s.members, data] }));
  },

  updateMemberRole: async (workspaceId, memberId, role) => {
    const data = await api.patch<TeamMember>(`/api/workspaces/${workspaceId}/members/${memberId}`, { role });
    set((s) => ({ members: s.members.map((m) => (m.id === memberId ? { ...m, ...data } : m)) }));
  },

  removeMember: async (workspaceId, memberId) => {
    await api.delete(`/api/workspaces/${workspaceId}/members/${memberId}`);
    set((s) => ({ members: s.members.filter((m) => m.id !== memberId) }));
  },
}));
